import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { sendHubspotEvent, usePortalUser } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { CompetencyQuestion } from 'src/constants/voicebox';
import { useCreateProjectState } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import { uiSlice } from 'src/store/ui/ui.slice';
import { createNewCloudProject } from 'src/utils/cloud/createNewCloudProject';
import { generateDateTimeId } from 'src/utils/generateId';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';

export const useCreateProject = () => {
  const isMounted = useIsMounted();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const {
    name: localName,
    description: localDescription,
    competencyQuestions: localCompetencyQuestions,
    resetState,
  } = useCreateProjectState();

  const defaultConnection = useDefaultPortalConnection();
  const portalUser = usePortalUser();
  const isCloudStorageEnabled = portalUser.data?.is_designer_storage_enabled;

  return React.useCallback(async () => {
    const name = localName.trim();
    if (!name) {
      return;
    }

    const description = localDescription.trim();
    const competencyQuestions = localCompetencyQuestions.reduce<
      CompetencyQuestion[]
    >((acc, question) => {
      const trimmed = question.question.trim();
      if (trimmed) {
        acc.push({ id: question.id, question: trimmed });
      }
      return acc;
    }, []);

    const id = generateDateTimeId();
    const modelId = uuid();
    const dateCreated = Date.now();

    let projectId = id;
    if (isCloudStorageEnabled) {
      // create a cloud project
      const cloudProjectId = await createNewCloudProject(
        { name, description, competencyQuestions },
        defaultConnection?.id
      );

      if (!cloudProjectId || !isMounted.current) {
        return;
      }
      projectId = cloudProjectId;
    } else {
      // create a local project
      dispatch(
        projectsActionCreators.addedProject({
          id,
          connectionId: defaultConnection?.id,
          modelId,
          name,
          description,
          competencyQuestions,
          dateCreated,
        })
      );
    }

    dispatch(uiSlice.actions.toggledCreateProjectOverlay({ isOpen: false }));
    resetState();
    sendHubspotEvent(TrackingEventList.DESIGNER_CREATE_PROJECT);

    navigate(getProjectRoute(projectId));
  }, [
    defaultConnection,
    dispatch,
    isCloudStorageEnabled,
    isMounted,
    localCompetencyQuestions,
    localDescription,
    localName,
    navigate,
    resetState,
  ]);
};
